/**
 * PointBuilder - Point Entity 생성을 위한 Builder
 */
import type { ICanvasEngineEntityController } from '../../../types/interface'
import type { Vector2d, CanvasEngineExportEntityType } from '../../../types'
import { SHAPES } from '../../../const'
import type { PointInitConfig } from './pointEntity'

/**
 * PointBuilder 클래스
 * Builder 패턴을 사용하여 Point Entity 생성 설정을 구성합니다.
 */
export class PointBuilder {
  private canvasEngine: ICanvasEngineEntityController
  private config: Partial<PointInitConfig> = {}

  /**
   * PointBuilder 생성자
   * @param canvasEngine - CanvasEngine 컨트롤러
   */
  constructor(canvasEngine: ICanvasEngineEntityController) {
    this.canvasEngine = canvasEngine
    this.reset()
  }

  /**
   * 설정 초기화
   * @returns this
   */
  reset(): PointBuilder {
    this.config = {
      interactionMode: true,
    }
    return this
  }

  /**
   * ID 설정
   * @param id - Entity ID
   * @returns this
   */
  setId(id: string): PointBuilder {
    this.config.id = id
    return this
  }

  /**
   * 생성 위치 설정
   * @param point - 위치 좌표
   * @returns this
   */
  setCreatePoint(point: Vector2d): PointBuilder {
    this.config.createPoint = point
    return this
  }

  /**
   * Balloon 위치 설정
   * @param point - Balloon 위치 좌표
   * @returns this
   */
  setBalloonPoint(point: Vector2d): PointBuilder {
    this.config.balloonPoint = point
    return this
  }

  /**
   * Point 색상 설정
   * @param color - 색상
   * @returns this
   */
  setColor(color: string): PointBuilder {
    if (!this.config.config) {
      this.config.config = { color }
    } else {
      this.config.config.color = color
    }
    return this
  }

  /**
   * Point 반지름 설정
   * @param radius - 반지름
   * @returns this
   */
  setRadius(radius: number): PointBuilder {
    if (!this.config.config) {
      this.config.config = { color: '#000000', radius }
    } else {
      this.config.config.radius = radius
    }
    return this
  }

  /**
   * 레이어 ID 설정
   * @param layerId - 레이어 ID
   * @returns this
   */
  setLayerId(layerId: string): PointBuilder {
    this.config.layerId = layerId
    return this
  }

  /**
   * Point 크기 스케일 설정
   * @param scale - 스케일
   * @returns this
   */
  setPointShapeScale(scale: number): PointBuilder {
    this.config.pointShapeScale = scale
    return this
  }

  /**
   * Balloon 글자색 설정
   * @param color - 색상
   * @returns this
   */
  setBalloonFontColor(color: string): PointBuilder {
    this.config.balloonFontColor = color
    return this
  }

  /**
   * Balloon 배경색 설정
   * @param color - 색상
   * @returns this
   */
  setBalloonFillColor(color: string): PointBuilder {
    this.config.balloonFillColor = color
    return this
  }

  /**
   * Balloon 스케일 설정
   * @param scale - 스케일
   * @returns this
   */
  setBalloonScale(scale: number): PointBuilder {
    this.config.balloonScale = scale
    return this
  }

  /**
   * 연결선 두께 스케일 설정
   * @param scale - 스케일
   * @returns this
   */
  setConnectedLineThicknessScale(scale: number): PointBuilder {
    this.config.connectedLineThicknessScale = scale
    return this
  }

  /**
   * Balloon 글자 스케일 설정
   * @param scale - 스케일
   * @returns this
   */
  setBalloonFontScale(scale: number): PointBuilder {
    this.config.balloonFontScale = scale
    return this
  }

  /**
   * 영역 테두리 두께 스케일 설정
   * @param scale - 스케일
   * @returns this
   */
  setAreaBorderThicknessScale(scale: number): PointBuilder {
    this.config.areaBorderThicknessScale = scale
    return this
  }

  /**
   * 상호작용 모드 설정
   * @param mode - 모드
   * @returns this
   */
  setInteractionMode(mode: boolean): PointBuilder {
    this.config.interactionMode = mode
    return this
  }

  /**
   * 점 도형 형태 설정 (dot 또는 arrow)
   * @param dotType - 'dot' 또는 'arrow'
   * @returns this
   */
  setDotType(dotType: 'dot' | 'arrow'): PointBuilder {
    this.config.dotType = dotType
    return this
  }

  /**
   * Export 데이터에서 설정 로드
   * @param data - Export 데이터
   * @returns this
   */
  fromExportData(data: CanvasEngineExportEntityType): PointBuilder {
    if (data.type !== SHAPES.POINT) {
      throw new Error('Invalid entity type for PointBuilder')
    }

    const builder = this.setId(data.shapeId)
      .setCreatePoint(data.point)
      .setBalloonPoint(data.balloonPoint)
      .setColor(data.color)
      .setRadius(this.canvasEngine.pointSize)
      .setLayerId(data.layerId)
      .setBalloonFontColor(data.balloonFontColor || '')
      .setBalloonFillColor(data.balloonFillColor || '')
      .setBalloonScale(data.balloonScale ?? 1)
      .setConnectedLineThicknessScale(data.connectedLineThicknessScale ?? 1)
      .setBalloonFontScale(data.balloonFontScale ?? 1)
      .setPointShapeScale(data.pointShapeScale || 1)
      .setAreaBorderThicknessScale(data.areaBorderThicknessScale || 1)

    if (data.dotType) builder.setDotType(data.dotType)

    return builder
  }

  /**
   * 설정 구성 반환
   * @returns PointInitConfig
   */
  build(): PointInitConfig {
    if (!this.config.createPoint) {
      throw new Error('createPoint is required')
    }
    if (!this.config.config) {
      throw new Error('config is required')
    }

    return {
      id: this.config.id,
      createPoint: this.config.createPoint,
      balloonPoint: this.config.balloonPoint,
      config: this.config.config,
      layerId: this.config.layerId,
      pointShapeScale: this.config.pointShapeScale,
      balloonFontColor: this.config.balloonFontColor,
      balloonFillColor: this.config.balloonFillColor,
      balloonScale: this.config.balloonScale,
      connectedLineThicknessScale: this.config.connectedLineThicknessScale,
      balloonFontScale: this.config.balloonFontScale,
      areaBorderThicknessScale: this.config.areaBorderThicknessScale,
      interactionMode: this.config.interactionMode,
      dotType: this.config.dotType,
    }
  }

  /**
   * CanvasEngine 반환
   * @returns CanvasEngine 컨트롤러
   */
  getCanvasEngine(): ICanvasEngineEntityController {
    return this.canvasEngine
  }
}
